import {Dispatch, SetStateAction, useEffect, useRef, useState} from 'react'
import {RIF, RequestResult, useTranslation} from '../../lib'
import {createDispatchActions, selectIdentity, selectTheme, SecurityChangeKind} from '../../store'
import {FreshAuthenticationProofPayload} from '../../model/auth_proof'
import {
  CONFIRM_ITS_YOU_EMPTY_VALUE,
  ConfirmItsYouFieldErrors,
  ConfirmItsYouValue,
  readConfirmItsYouOutcome,
} from '../molecules/confirm_its_you_fields'
import {ConfirmItsYouFields, PopupBackground, PopupSimple} from '..'

export interface SecurityChangeConfirmPopPropsT {
  /** Which security setting is being saved; it is what a later sign-out is named after. */
  kind: SecurityChangeKind
  /**
   * Fire the security-change request. The pop hands the host its own `setRequestResult` and keeps
   * the outcome: the neutral success line, the failure line and the `lastSecurityChange` mark all
   * live here, so every host gets them. `proof` is what the confirm step collected -- spread it
   * into the request body.
   */
  onContinue: (arg: {
    setRequestResult: Dispatch<SetStateAction<RequestResult | null>>
    proof: FreshAuthenticationProofPayload
  }) => void
  /** Dismissed without saving: the host puts its control back the way it was. */
  onCancel: () => void
  /** Closed after a save that succeeded: the host keeps the new value. */
  onDone: () => void
}

/**
 * The confirm prompt as the MFA toggle opens it. It mounts `ConfirmItsYouFields`, sends what they
 * collect with the request and shows the backend's 403 hints inline without closing,
 * so a wrong password costs a retry rather than the whole dialog.
 */
export const SecurityChangeConfirmPop = (props: SecurityChangeConfirmPopPropsT) => {
  const {kind, onContinue, onCancel, onDone} = props
  const {t} = useTranslation()
  const {color} = selectTheme()
  const {doSESSION_LAST_SECURITY_CHANGE_SET} = createDispatchActions()
  const identity = selectIdentity()

  // The identity's CURRENT setting, not the one being saved: turning MFA on is a change no
  // authenticator code can back yet, and turning it off is one that can.
  const mfaEnabled = identity?.mfaEnabled ?? false

  const [requestResult, setRequestResult] = useState<RequestResult | null>(null)
  const [saved, setSaved] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<ConfirmItsYouFieldErrors>({})
  const [waiting, setWaiting] = useState(false)
  const [confirmValue, setConfirmValue] = useState<ConfirmItsYouValue>(CONFIRM_ITS_YOU_EMPTY_VALUE)

  // `PopupSimple` binds its Escape handler in a `useEffect` with `[]` deps, so it keeps calling
  // whichever `closeAction` it saw on the first render: swapping the prop while a save is in
  // flight would close the X and the Cancel link and leave Escape wide open. The guard therefore
  // has to be inside the callback, reading something the captured closure can still see change.
  const savingRef = useRef(false)
  // Whether the save is marked for the later sign-out message depends on the proof that was SENT,
  // and the fields stay editable while the request is out, so the live `confirmValue` at reply time
  // is the wrong thing to ask. Only the verdict is kept, never the proof itself: the password and
  // the code stay in the molecule and nowhere else.
  const sentProofWasCompleteRef = useRef(false)

  useEffect(() => {
    if (!requestResult) return
    savingRef.current = false
    setWaiting(false)

    // The email page reads a refused or incomplete proof the same way, so both use one reader.
    const outcome = readConfirmItsYouOutcome(requestResult, {
      sentProofWasComplete: sentProofWasCompleteRef.current,
      t,
    })

    if (outcome.kind === 'proof_refused') {
      setFieldErrors(outcome.fieldErrors)
      return setFormError(outcome.formError)
    }
    // Every other failure is one line here. The email flow tells four of them apart, which is why
    // the shared reading stops short of this and hands the reply back.
    if (outcome.kind === 'unhandled') return setFormError(t('security_change.error_message'))

    // Nothing is signed out from here -- after activation the next request's 401 drives the
    // existing path.
    if (outcome.recordIncompleteProof) doSESSION_LAST_SECURITY_CHANGE_SET({kind, at: Date.now()})
    // the request that carried the proof has been answered, so this copy of it goes now
    // rather than waiting for the pop to close.
    setConfirmValue(CONFIRM_ITS_YOU_EMPTY_VALUE)
    setSaved(true)
  }, [requestResult])

  const handleContinue = () => {
    setFormError(null)
    setFieldErrors({})
    savingRef.current = true
    setWaiting(true)
    sentProofWasCompleteRef.current = confirmValue.proofIsComplete
    onContinue({setRequestResult, proof: confirmValue.proof})
  }

  /**
   * Cancelling puts the host's control back, but nothing here can call the request back. Dismissing a
   * save that is already out would unmount the pop and revert the control while the saga went on
   * to apply the change -- a cancel the user believes in and the backend never hears about. So
   * every exit is closed for exactly the length of the request, and no longer.
   */
  const handleCancel = () => {
    if (savingRef.current) return
    onCancel()
  }

  const submitBlocked = waiting || !confirmValue.canSubmit

  return (
    <PopupBackground>
      {saved ? (
        <PopupSimple
          key='security_change_saved'
          data-testid='security_change_saved_pop'
          name={t('security_change.saved_title')}
          // "may be asked to sign in again", never a claim that other devices were signed out
          description={t('security_change.saved_message')}
          buttonText={t('security_change.done_button')}
          cancelText={t('security_change.close_button')}
          closeAction={onDone}
          doAction={onDone}
          type='button'
        />
      ) : (
        <PopupSimple
          key='security_change_confirm'
          data-testid='security_change_confirm_pop'
          name={t('security_change.confirm_title')}
          // The proof is what keeps the other sessions alive, so a blanket "you may be signed out
          // everywhere" warning would not be true here. What remains true for an MFA identity is the
          // narrower SMS/email warning, which the fields carry.
          description={t('security_change.confirm_intro')}
          buttonText={t('security_change.continue_button')}
          cancelText={t('security_change.cancel_button')}
          closeAction={handleCancel}
          doAction={handleContinue}
          btnDisableAction={submitBlocked}
          type='button'
          html={
            // a real form, so a password manager offers the right credential. Continue is
            // `PopupSimple`'s own button and sits outside it, so Enter inside a field would
            // otherwise implicitly submit a form with no action and reload the page.
            <form
              data-testid='security_change_confirm_form'
              onSubmit={(event) => {
                event.preventDefault()
                if (!submitBlocked) handleContinue()
              }}>
              <ConfirmItsYouFields
                {...{
                  mfaEnabled,
                  fieldErrors,
                  onChange: setConfirmValue,
                }}
              />
              {RIF(
                formError,
                <div data-testid='security_change_error' css={{color: color.warning}}>
                  {formError}
                </div>,
              )}
            </form>
          }
        />
      )}
    </PopupBackground>
  )
}
